<template>
  <Page :title="{ name: 'Advanced Search', backLocation: href.home }">
    <NamiInput v-model="query.search" block placeholder="Search..." />
    <NamiButton
      button-type="secondary"
      text
      no-waves
      :icon="showFilters ? IconChevronDown : IconChevronUp"
      icon-position="right"
      @button-click="showFilters = !showFilters"
    >
      <h2 class="text-lg font-medium">Filters</h2>
    </NamiButton>
    <TransitionAutoHeight v-model="showFilters">
      <div class="grid gap-4 lg:grid-cols-4 md:grid-cols-3 grid-cols-2">
        <SelectModalSingle
          v-model="query.sort"
          :options="sortOptions"
          label="Sort by"
        />
        <SelectModalTristate
          v-model:included="query.includedGenres"
          v-model:excluded="query.excludedGenres"
          v-model:included-mode="query.includedTagsMode"
          v-model:excluded-mode="query.excludedTagsMode"
          :options="
            tags?.genres.map((tag) => ({
              value: tag.id,
              text: t(`components.tag.${tag.id}.name`),
            })) ?? []
          "
          label="Genres"
          :loading="tagsLoading"
        />
        <SelectModalTristate
          v-model:included="query.includedThemes"
          v-model:excluded="query.excludedThemes"
          v-model:included-mode="query.includedTagsMode"
          v-model:excluded-mode="query.excludedTagsMode"
          :options="
            tags?.themes.map((tag) => ({
              value: tag.id,
              text: t(`components.tag.${tag.id}.name`),
            })) ?? []
          "
          label="Themes"
          :loading="tagsLoading"
        />
        <SelectModalWithMany
          :fields="[
            {
              type: 'multiple',
              label: 'Reading Mode',
              props: {
                modelValue: query.readingModes,
                options: [
                  {
                    text: 'Any',
                    value: 'any',
                    description: 'All reading modes are accepted.',
                    exclusive: true,
                  },
                  ...readingModeOptions,
                ],
              },
            },
            {
              type: 'tristate',
              label: 'Format',
              props: {
                included: query.includedFormats,
                excluded: query.excludedFormats,
                includedMode: query.includedTagsMode,
                excludedMode: query.excludedTagsMode,
                options:
                  tags?.formats.map((tag) => ({
                    value: tag.id,
                    text: t(`components.tag.${tag.id}.name`),
                    description: t(`components.tag.${tag.id}.description`),
                  })) ?? [],
              },
            },
          ]"
          @update="
            ([index, key, value]) => {
              switch (index) {
                case 0: {
                  query.readingModes = value;

                  break;
                }

                case 1: {
                  switch (key) {
                    case 'included':
                      return (query.includedFormats = value);
                    case 'excluded':
                      return (query.excludedFormats = value);
                    case 'includedMode':
                      return (query.includedTagsMode = value);
                    case 'excludedMode':
                      return (query.excludedTagsMode = value);
                  }

                  break;
                }
              }
            }
          "
          label="Reading Mode and Format"
          :loading="tagsLoading"
        />
        <SelectModalTristate
          v-model:included="query.includedContent"
          v-model:excluded="query.excludedContent"
          v-model:included-mode="query.includedTagsMode"
          v-model:excluded-mode="query.excludedTagsMode"
          :options="
            tags?.content.map((tag) => ({
              value: tag.id,
              text: t(`components.tag.${tag.id}.name`),
            })) ?? []
          "
          label="Content Warnings"
          :loading="tagsLoading"
        />
        <SelectModalMultiple
          v-model="query.originalLanguages"
          :options="languageOptions"
          label="Original language"
          placeholder="Any"
        />
        <SelectModalMultiple
          v-model="query.chapterLanguages"
          :options="[
            {
              text: 'Any language',
              value: 'any',
              description: 'Will show titles with chapters in any language.',
              exclusive: true,
            },
            ...languageOptions,
          ]"
          label="Chapter languages"
          placeholder="Not specified"
        />
        <SelectModalMultiple
          v-model="query.publicationStatuses"
          :options="publicationStatusOptions"
          label="Publication status"
          placeholder="Any"
        />
        <SelectModalWithMany
          label="Advanced"
          placeholder="Nothing selected"
          :fields="[
            {
              type: 'single',
              label: 'Triple-state inclusion mode',
              props: {
                modelValue: query.includedTagsMode,
                options: [
                  {
                    text: 'And',
                    value: 'and',
                    description:
                      'All the included tags must be present on the title to match.',
                  },
                  {
                    text: 'Or',
                    value: 'or',
                    description:
                      'Any of the included tags can be present on the title to match.',
                  },
                ],
              },
            },
            {
              type: 'single',
              label: 'Triple-state exclusion mode',
              props: {
                modelValue: query.excludedTagsMode,
                options: [
                  {
                    text: 'And',
                    value: 'and',
                    description:
                      'Any titles that include the combination of the excluded tags are filtered out.',
                  },
                  {
                    text: 'Or',
                    value: 'or',
                    description:
                      'Any titles that have at least one of the excluded tags are filtered out.',
                  },
                ],
              },
            },
          ]"
          @update="
            ([index, _, value]) => {
              switch (index) {
                case 0:
                  return (query.includedTagsMode = value);
                case 1:
                  return (query.excludedTagsMode = value);
              }
            }
          "
        />
      </div>
    </TransitionAutoHeight>
    <div class="flex justify-end gap-2">
      <NamiButton
        button-type="danger"
        text
        :icon="IconRefresh"
        :disabled="isFilterInDefaultState"
        @button-click="resetFilters"
        :tooltip="
          isFilterInDefaultState
            ? 'Filters are in their default state'
            : 'Reset filters'
        "
      />
      <NamiButton
        button-type="primary"
        :icon="IconSearch"
        :disabled="pending"
        @button-click="doSearch"
      >
        Search
      </NamiButton>
    </div>
    <p>
      <span
        v-for="part in queryAnalysis"
        :class="{
          'text-nami-comi-blue': part.highlight === 'blue',
          'text-red-500': part.highlight === 'red',
          'text-green-500': part.highlight === 'green',
        }"
      >
        {{ part.text }}
      </span>
    </p>
    <NamiDivider />
    <Error v-if="!pending && error" :error="error" @retry="refresh" />
    <TheSearchNoResults v-else-if="!pending && data?.data.length === 0" />
    <TitlesDisplayCover
      v-else
      :titles="
        pending
          ? undefined
          : data?.data.map((title) => ({
              title: extractFromLocale(locale, title.attributes.title),
              coverUrl: selectLocalizedCover(locale, title, '256'),
              href: linkTo(title),
            }))
      "
    />
    <NamiPagination
      v-if="!pending && data"
      v-model="page"
      :total-pages="Math.ceil(data.meta.total / limit)"
      :total-items="data.meta.total"
    />
  </Page>
</template>

<script setup lang="ts">
import {
  Title,
  Tag,
  type TitleFuzzyListOrderOptions,
  type AvailableLanguages,
  type PublicationStatus,
  type TitleReadingMode,
} from "~/src/api";
import deepEqual from "deep-eql";
import {
  IconSearch,
  IconChevronDown,
  IconChevronUp,
  IconRefresh,
} from "@tabler/icons-vue";

type MakeAscDescCombos<T extends string> = `${T}.asc` | `${T}.desc`;

const href = useHref();
const route = useRoute();
const { page, limit, offset } = usePagination();
const { selectLocalizedCover } = useMediaLink();

const showFilters = ref(true);
const { locale, t } = useNuxtApp().$i18n.global;

const sortOptions: {
  text: string;
  value: MakeAscDescCombos<
    Exclude<TitleFuzzyListOrderOptions, "createdAt" | "updatedAt">
  >;
  description: string;
}[] = [
  {
    text: "Title (A to Z)",
    value: "titles.asc",
    description: "Sort items alphabetically by title from A to Z.",
  },
  {
    text: "Title (Z to A)",
    value: "titles.desc",
    description:
      "Sort items in reverse alphabetical order by title from Z to A.",
  },
  {
    text: "Most Views",
    value: "views.desc",
    description:
      "Sort items based on the number of total views, with the highest ones at the top.",
  },
  {
    text: "Least Views",
    value: "views.asc",
    description:
      "Sort items based on the number of total views, with the lowest ones at the top.",
  },
  {
    text: "Most Followers",
    value: "followCount.desc",
    description:
      "Sort items by the number of followers, from the most to the least.",
  },
  {
    text: "Least Followers",
    value: "followCount.asc",
    description:
      "Sort items by the number of followers, from the least to the most.",
  },
  {
    text: "Most Likes",
    value: "reactions.desc",
    description:
      "Sort items by the number of likes, from the most to the least.",
  },
  {
    text: "Least Likes",
    value: "reactions.asc",
    description:
      "Sort items by the number of likes, from the least to the most.",
  },
  {
    text: "Highest Rating",
    value: "rating.desc",
    description: "Sort items by their rating, from the highest to the lowest.",
  },
  {
    text: "Lowest Rating",
    value: "rating.asc",
    description: "Sort items by their rating, from the lowest to the highest.",
  },
  {
    text: "Year Published (Newest First)",
    value: "year.desc",
    description:
      "Sort items by their publication year, from the most recent to the oldest.",
  },
  {
    text: "Year Published (Oldest First)",
    value: "year.asc",
    description:
      "Sort items by their publication year, from the oldest to the most recent.",
  },
  {
    text: "Most Chapters",
    value: "chapterCount.desc",
    description:
      "Sort items by the number of chapters, from the most to the least.",
  },
  {
    text: "Least Chapters",
    value: "chapterCount.asc",
    description:
      "Sort items by the number of chapters, from the least to the most.",
  },
  {
    text: "Most Comments",
    value: "commentCount.desc",
    description:
      "Sort items by the number of comments, from the most to the least.",
  },
  {
    text: "Least Comments",
    value: "commentCount.asc",
    description:
      "Sort items by the number of comments, from the least to the most.",
  },
  {
    text: "Latest Published",
    value: "publishedAt.desc",
    description:
      "Sort items by their publication date, from the latest to the earliest.",
  },
  {
    text: "Earliest Published",
    value: "publishedAt.asc",
    description:
      "Sort items by their publication date, from the earliest to the latest.",
  },
];

const languageOptions = getLanguages();

const publicationStatusOptions: {
  text: string;
  value: PublicationStatus;
  description: string;
}[] = [
  {
    text: "Ongoing",
    value: "ongoing",
    description:
      "The series is currently being published and has not yet reached its conclusion.",
  },
  {
    text: "Completed",
    value: "completed",
    description:
      "The series has finished publication and the story has concluded.",
  },
  {
    text: "Hiatus",
    value: "hiatus",
    description:
      "Publication of the series has been temporarily paused or suspended.",
  },
  {
    text: "Cancelled",
    value: "cancelled",
    description:
      "The series has been discontinued before reaching its planned conclusion.",
  },
];

const readingModeOptions: {
  text: string;
  value: "horizontal" | "longstrip";
  description: string;
}[] = [
  {
    text: "Comic/Manga",
    value: "horizontal",
    description: "",
  },
  {
    text: "Longstrip",
    value: "longstrip",
    description: "",
  },
];

const {
  search,
  sort,
  includedGenres,
  includedThemes,
  includedFormats,
  includedContent,
  excludedGenres,
  excludedThemes,
  excludedFormats,
  excludedContent,
  includedMode,
  excludedMode,
  originalLanguages,
  chapterLanguages,
  publicationStatuses,
  readingModes,
} = useRouteQueryTemplate({
  search: {
    type: "string",
    default: "",
  },
  sort: {
    type: "string",
    default: "titles.asc",
  },
  includedGenres: {
    type: "string.array",
    default: [],
  },
  includedThemes: {
    type: "string.array",
    default: [],
  },
  includedFormats: {
    type: "string.array",
    default: [],
  },
  includedContent: {
    type: "string.array",
    default: [],
  },
  excludedGenres: {
    type: "string.array",
    default: [],
  },
  excludedThemes: {
    type: "string.array",
    default: [],
  },
  excludedFormats: {
    type: "string.array",
    default: [],
  },
  excludedContent: {
    type: "string.array",
    default: [],
  },
  includedMode: {
    type: "string",
    default: "and",
  },
  excludedMode: {
    type: "string",
    default: "or",
  },
  originalLanguages: {
    type: "string.array",
    default: [],
  },
  chapterLanguages: {
    type: "string.array",
    default: [],
  },
  publicationStatuses: {
    type: "string.array",
    default: [],
  },
  readingModes: {
    type: "string.array",
    default: ["any"],
  },
});

const query = reactive({
  search: search.value,
  sort: sort.value as MakeAscDescCombos<
    Exclude<TitleFuzzyListOrderOptions, "createdAt" | "updatedAt">
  >,
  includedGenres: includedGenres.value,
  includedThemes: includedThemes.value,
  includedFormats: includedFormats.value,
  includedContent: includedContent.value,
  excludedGenres: excludedGenres.value,
  excludedThemes: excludedThemes.value,
  excludedFormats: excludedFormats.value,
  excludedContent: excludedContent.value,
  includedTagsMode: includedMode.value as "and" | "or",
  excludedTagsMode: excludedMode.value as "and" | "or",
  originalLanguages: originalLanguages.value as AvailableLanguages[],
  publicationStatuses: publicationStatuses.value as PublicationStatus[],
  chapterLanguages: chapterLanguages.value as (AvailableLanguages | "any")[],
  readingModes: readingModes.value as ("horizontal" | "longstrip" | "any")[],
});

const isFilterInDefaultState = computed(() =>
  deepEqual(query, {
    search: "",
    sort: "titles.asc",
    includedGenres: [],
    includedThemes: [],
    includedFormats: [],
    includedContent: [],
    excludedGenres: [],
    excludedThemes: [],
    excludedFormats: [],
    excludedContent: [],
    includedTagsMode: "and",
    excludedTagsMode: "or",
    originalLanguages: [],
    publicationStatuses: [],
    chapterLanguages: [],
    readingModes: ["any"],
  }),
);

const queryAnalysis = computed(() => {
  const analysisString: {
    text: string;
    highlight?: "green" | "red" | "blue";
  }[] = [];

  function addText(text: string, highlight?: "green" | "red" | "blue") {
    analysisString.push({ text, highlight });
  }

  addText("You're searching for");

  if (query.search) {
    addText(' titles that contain "');
    addText(query.search, "blue");
    addText(',"');
  } else {
    addText(" all titles,");
  }

  addText(" sorted by");

  const sortString = (() => {
    switch (query.sort) {
      case "chapterCount.asc":
        return " least chapters";
      case "chapterCount.desc":
        return " most chapters";
      case "commentCount.asc":
        return " least comments";
      case "commentCount.desc":
        return " most comments";
      case "followCount.asc":
        return " least followers";
      case "followCount.desc":
        return " most followers";
      case "latestUploadedChapter.asc":
        return " those that have had a chapter uploaded most recently";
      case "latestUploadedChapter.desc":
        return " those that have had a chapter uploaded the earliest";
      case "publishedAt.asc":
        return " the earliest publish date";
      case "publishedAt.desc":
        return " the latest publish date";
      case "rating.asc":
        return " the lowest rating";
      case "rating.desc":
        return " the highest rating";
      case "titles.asc":
        return " title from A to Z";
      case "titles.desc":
        return " title from Z to A";
      case "views.asc":
        return " least views";
      case "views.desc":
        return " most views";
      case "year.asc":
        return " earliest year published";
      case "year.desc":
        return " latest year published";
      case "reactions.asc":
        return " least likes";
      case "reactions.desc":
        return " most likes";
      default: {
        const lang = query.sort.split(".")[1] as AvailableLanguages;
        return ` most viewed chapters in language "${t(`languages.${lang}`)}"`;
      }
    }
  })();

  addText(sortString, "blue");

  const includedGenresAndThemes = [
    ...query.includedGenres,
    ...query.includedThemes,
  ];

  const excludedGenresAndThemes = [
    ...query.excludedGenres,
    ...query.excludedThemes,
  ];

  if (includedGenresAndThemes.length > 0) {
    addText(" and include");

    if (query.includedTagsMode === "or" && includedGenresAndThemes.length > 1) {
      addText(" any of");
    }

    const shouldIncludeCommaInTheEnd = includedGenresAndThemes.length > 2;

    includedGenresAndThemes.forEach((genre, i) => {
      const tagName = t(`components.tag.${genre}.name`).toLocaleLowerCase(
        "en-US",
      );

      addText(` ${tagName}`, "green");

      if (i + 1 < includedGenresAndThemes.length - 1) {
        addText(",");
      } else if (i + 1 === includedGenresAndThemes.length - 1) {
        addText(
          `${shouldIncludeCommaInTheEnd ? "," : ""} ${query.includedTagsMode}`,
        );
      }
    });
  }

  if (excludedGenresAndThemes.length > 0) {
    if (includedGenresAndThemes.length > 0) {
      addText(" but do not include");
    } else {
      addText(" and do not include");
    }

    if (
      query.excludedTagsMode === "and" &&
      excludedGenresAndThemes.length > 1
    ) {
      addText(" the combination of");
    }

    const shouldIncludeCommaInTheEnd = excludedGenresAndThemes.length > 2;

    excludedGenresAndThemes.forEach((genre, i) => {
      const tagName = t(`components.tag.${genre}.name`).toLocaleLowerCase(
        "en-US",
      );

      addText(` ${tagName}`, "red");

      if (i + 1 < excludedGenresAndThemes.length - 1) {
        addText(",");
      } else if (i + 1 === excludedGenresAndThemes.length - 1) {
        addText(
          `${shouldIncludeCommaInTheEnd ? "," : ""} ${query.excludedTagsMode}`,
        );
      }
    });
  }

  addText(".");

  if (query.includedFormats.length > 0) {
    if (query.includedTagsMode === "and") {
      addText(" It must be");
    } else {
      addText(" It can be");
    }

    const shouldIncludeCommaInTheEnd = query.includedFormats.length > 2;

    query.includedFormats.forEach((format, i) => {
      const tagName = t(`components.tag.${format}.name`).toLocaleLowerCase(
        "en-US",
      );

      addText(` ${tagName}`, "green");

      if (i + 1 < query.includedFormats.length - 1) {
        addText(",");
      } else if (i + 1 === query.includedFormats.length - 1) {
        addText(
          `${shouldIncludeCommaInTheEnd ? "," : ""} ${query.includedTagsMode}`,
        );
      }
    });

    if (
      query.includedContent.length === 0 &&
      query.excludedContent.length === 0 &&
      query.excludedFormats.length === 0
    ) {
      addText(".");
    }
  }

  if (query.excludedFormats.length > 0) {
    if (query.includedFormats.length > 0) {
      addText(" but not");
    } else {
      addText(" It must not be");
    }

    if (query.excludedTagsMode === "and" && query.excludedFormats.length > 1) {
      addText(" the combination of");
    }

    const shouldIncludeCommaInTheEnd = query.excludedFormats.length > 2;

    query.excludedFormats.forEach((format, i) => {
      const tagName = t(`components.tag.${format}.name`).toLocaleLowerCase(
        "en-US",
      );

      addText(` ${tagName}`, "red");

      if (i + 1 < query.excludedFormats.length - 1) {
        addText(",");
      } else if (i + 1 === query.excludedFormats.length - 1) {
        addText(
          `${shouldIncludeCommaInTheEnd ? "," : ""} ${query.excludedTagsMode}`,
        );
      }
    });

    if (
      query.includedContent.length === 0 &&
      query.excludedContent.length === 0
    ) {
      addText(".");
    }
  }

  if (query.includedContent.length > 0) {
    if (
      query.includedFormats.length === 0 &&
      query.excludedFormats.length === 0
    ) {
      addText(" It");
    } else {
      addText(". It");
    }

    if (query.includedTagsMode === "and") {
      addText(" must include");
    } else {
      addText(" can include");
    }

    const shouldIncludeCommaInTheEnd = query.includedContent.length > 2;

    query.includedContent.forEach((content, i) => {
      const tagName = t(`components.tag.${content}.name`).toLocaleLowerCase(
        "en-US",
      );

      addText(` ${tagName}`, "green");

      if (i + 1 < query.includedContent.length - 1) {
        addText(",");
      } else if (i + 1 === query.includedContent.length - 1) {
        addText(
          `${shouldIncludeCommaInTheEnd ? "," : ""} ${query.includedTagsMode}`,
        );
      }
    });

    if (query.excludedContent.length === 0) {
      addText(".");
    }
  }

  if (query.excludedContent.length > 0) {
    if (query.includedContent.length > 0) {
      addText(" but not");
    } else {
      if (query.includedFormats.length > 0) {
        if (query.excludedFormats.length > 0) {
          addText(", while also not including");
        } else {
          addText(", but not include");
        }
      } else {
        addText(" It should not include");
      }
    }

    if (query.excludedTagsMode === "and" && query.excludedContent.length > 1) {
      addText(" the combination of");
    }

    const shouldIncludeCommaInTheEnd = query.excludedContent.length > 2;

    query.excludedContent.forEach((content, i) => {
      const tagName = t(`components.tag.${content}.name`).toLocaleLowerCase(
        "en-US",
      );

      addText(` ${tagName}`, "red");

      if (i + 1 < query.excludedContent.length - 1) {
        addText(",");
      } else if (i + 1 === query.excludedContent.length - 1) {
        addText(
          `${shouldIncludeCommaInTheEnd ? "," : ""} ${query.excludedTagsMode}`,
        );
      }
    });

    addText(".");
  }

  if (query.originalLanguages.length > 0) {
    addText(" Its original language");

    if (query.originalLanguages.length > 1) {
      addText(" can be");
    } else {
      addText(" is");
    }

    const shouldIncludeCommaInTheEnd = query.originalLanguages.length > 2;

    query.originalLanguages.forEach((lang, i) => {
      const tagName =
        languageOptions.find((x) => x.value === lang)?.text ?? "???";

      addText(` ${tagName}`, "blue");

      if (i + 1 < query.originalLanguages.length - 1) {
        addText(",");
      } else if (i + 1 === query.originalLanguages.length - 1) {
        addText(`${shouldIncludeCommaInTheEnd ? "," : ""} or`);
      }
    });

    if (query.chapterLanguages.length === 0) {
      addText(".");
    }
  }

  if (query.chapterLanguages.length > 0) {
    if (query.originalLanguages.length > 0) {
      addText(", and it needs to have chapters");
    } else {
      addText(" It needs to have chapters");
    }

    addText(" in");

    if (query.chapterLanguages[0] === "any") {
      addText(" any language");
    }

    const chapterLanguagesWithoutAny = query.chapterLanguages.filter(
      (x) => x !== "any",
    );

    const shouldIncludeCommaInTheEnd = chapterLanguagesWithoutAny.length > 2;

    chapterLanguagesWithoutAny.forEach((lang, i) => {
      const tagName =
        languageOptions.find((x) => x.value === lang)?.text ?? "???";

      addText(` ${tagName}`, "blue");

      if (i + 1 < chapterLanguagesWithoutAny.length - 1) {
        addText(",");
      } else if (i + 1 === chapterLanguagesWithoutAny.length - 1) {
        addText(`${shouldIncludeCommaInTheEnd ? "," : ""} or`);
      }
    });

    addText(".");
  }

  if (query.publicationStatuses.length > 0) {
    addText(" Its publication");

    if (query.publicationStatuses.length > 1) {
      addText(" can be");
    } else {
      addText(" is");
    }

    const shouldIncludeCommaInTheEnd = query.publicationStatuses.length > 2;

    query.publicationStatuses.forEach((status, i) => {
      addText(` ${status}`, "blue");

      if (i + 1 < query.publicationStatuses.length - 1) {
        addText(",");
      } else if (i + 1 === query.publicationStatuses.length - 1) {
        addText(`${shouldIncludeCommaInTheEnd ? "," : ""} or`);
      }
    });

    addText(".");
  }

  if (query.readingModes.length > 0 && query.readingModes[0] !== "any") {
    addText(" Its reading mode");

    if (query.readingModes.length > 1) {
      addText(" can be");
    } else {
      addText(" is");
    }

    const shouldIncludeCommaInTheEnd = query.readingModes.length > 2;

    query.readingModes.forEach((mode, i) => {
      const tagName = t(`entity.title.readingMode.${mode}`);

      addText(` ${tagName}`, "blue");

      if (i + 1 < query.readingModes.length - 1) {
        addText(",");
      } else if (i + 1 === query.readingModes.length - 1) {
        addText(`${shouldIncludeCommaInTheEnd ? "," : ""} or`);
      }
    });

    addText(".");
  }

  return analysisString;
});

function resetFilters() {
  query.search = "";
  query.sort = "titles.asc";
  query.includedGenres = [];
  query.includedThemes = [];
  query.includedFormats = [];
  query.includedContent = [];
  query.excludedGenres = [];
  query.excludedThemes = [];
  query.excludedFormats = [];
  query.excludedContent = [];
  query.includedTagsMode = "and";
  query.excludedTagsMode = "or";
  query.originalLanguages = [];
  query.chapterLanguages = [];
  query.publicationStatuses = [];
  query.readingModes = ["any"];
}

const { pending: tagsLoading, data: tags } = useAsyncData(
  "tags",
  async () => {
    const tagList = await Tag.getTagList().then((x) => x.data);

    return {
      genres: tagList.filter((x) => x.attributes.group === "genre"),
      themes: tagList.filter((x) => x.attributes.group === "theme"),
      formats: tagList.filter((x) => x.attributes.group === "format"),
      content: tagList.filter(
        (x) =>
          x.attributes.group === "content" ||
          x.attributes.group === "content-warnings",
      ),
    };
  },
  { server: false },
);

const { pending, data, error, refresh } = useAsyncData(
  `title-search`,
  async () => {
    const [orderCategory, orderMethod] = query.sort.split(".") as [
      TitleFuzzyListOrderOptions,
      "asc" | "desc",
    ];

    const finalReadingModes = query.readingModes
      .map<TitleReadingMode[]>((mode) => {
        if (mode === "longstrip") return ["vls"];
        if (mode === "horizontal") return ["ltr", "rtl"];
        // Since 'any' is exclusive, our component will ensure that
        // it will be the only element inside the array,
        // in this case, we will return an empty array
        else return [];
      })
      .flat();

    return await Title.fuzzySearch({
      title: query.search,
      order: { [orderCategory]: orderMethod },
      includedTags: [
        ...query.includedGenres,
        ...query.includedThemes,
        ...query.includedFormats,
        ...query.includedContent,
      ],
      excludedTags: [
        ...query.excludedGenres,
        ...query.excludedThemes,
        ...query.excludedFormats,
        ...query.excludedContent,
      ],
      includedTagsMode: query.includedTagsMode,
      excludedTagsMode: query.excludedTagsMode,
      originalLanguages: query.originalLanguages,
      publicationStatuses: query.publicationStatuses,
      hasAvailableChapters: query.chapterLanguages.includes("any")
        ? true
        : undefined,
      availableTranslatedLanguages: query.chapterLanguages.filter(
        <T,>(x: T | "any"): x is T => x !== "any",
      ),
      readingModes: finalReadingModes,
      includes: ["cover_art"],
      contentRatings: ["mature", "restricted", "safe"],
      offset: offset.value,
      limit: limit.value,
    });
  },
  { server: false, watch: [() => route.query] },
);

async function doSearch() {
  navigateTo({
    query: Object.fromEntries(
      Object.entries(query).filter(([_, value]) => !!value),
    ),
  });
}

const nuxtApp = useNuxtApp();

const seo = {
  title: `Search Titles`,
  description: `Find titles on NamiComi.`,
  //slug: data.attributes.slug,
  coverUrl: "",
  href: `/en/titles/search`,
};

nuxtApp.runWithContext(() =>
  useSeoMeta({
    title: seo.title,
    ogTitle: seo.title,
    twitterTitle: seo.title,
    description: seo.description,
    ogDescription: seo.description,
    twitterDescription: seo.description,
    ogUrl: seo.href,
    ogLocale: locale.value,
    ogImage: seo.coverUrl,
    ogImageSecureUrl: seo.coverUrl,
    twitterImage: seo.coverUrl,
    //ogImageHeight: "1200",
    //ogImageWidth: "630",
    ogImageUrl: seo.coverUrl,
    //ogImageType: "image/png",
    ogImageAlt: "NamiComi",
    ogLocaleAlternate: "en_GB",
  }),
);
</script>
